import {
  surchargeLadderCreateSchema,
  type SurchargeLadderCreateInput,
} from '@hirebotiq-ops/core';

export type LadderStepDraft = {
  /** Auslöser-Art: vollendete Wochen oder vollendete Monate. */
  triggerType: 'weeks' | 'months';
  threshold: string;
  percent: string;
};

export type SurchargeLadderDraft = {
  name: string;
  base: 'wage' | 'bill_rate';
  clientId?: string;
  /** EG-Block-Scope (TV-BZ-Blöcke); leer = gilt für alle Entgeltgruppen. */
  entgeltGroups: string[];
  validFrom: string;
  notes: string;
  steps: LadderStepDraft[];
};

/** "eg2a" → "EG 2a" für Checkboxen und Scope-Anzeige. */
export function egLabel(value: string): string {
  return `EG ${value.replace(/^eg/, '')}`;
}

export function emptyLadderStepDraft(): LadderStepDraft {
  return { triggerType: 'weeks', threshold: '0', percent: '' };
}

function parseIntStrict(value: string, label: string): number {
  const parsed = Number(value.trim());
  if (!Number.isInteger(parsed) || parsed < 0) {
    throw new Error(`${label} muss eine ganze Zahl >= 0 sein.`);
  }
  return parsed;
}

export function buildSurchargeLadderInput(draft: SurchargeLadderDraft): SurchargeLadderCreateInput {
  const input = {
    name: draft.name.trim(),
    base: draft.base,
    ...(draft.clientId ? { clientId: draft.clientId } : {}),
    ...(draft.entgeltGroups.length > 0 ? { entgeltGroups: draft.entgeltGroups } : {}),
    validFrom: draft.validFrom,
    ...(draft.notes.trim() ? { notes: draft.notes.trim() } : {}),
    steps: draft.steps.map((step, index) => {
      const threshold = parseIntStrict(step.threshold, `Stufe ${index + 1}: Auslöser`);
      // Number('') wäre 0 — ein leeres Prozentfeld darf nicht still 0 % werden.
      const percentRaw = step.percent.trim();
      const percent = Number(percentRaw);
      if (percentRaw === '' || !Number.isFinite(percent)) {
        throw new Error(`Stufe ${index + 1}: Prozentwert fehlt oder ist keine Zahl.`);
      }
      return {
        ...(step.triggerType === 'weeks'
          ? { afterCompletedWeeks: threshold }
          : { afterCompletedMonths: threshold }),
        percent,
      };
    }),
  };

  return surchargeLadderCreateSchema.parse(input);
}

/** Kompakte Treppen-Anzeige: "ab Start 5 % → 3 M 10 % → 9 M 20 %". */
export function formatLadderSteps(
  steps: ReadonlyArray<{
    afterCompletedWeeks: number | null;
    afterCompletedMonths: number | null;
    percent: string | number;
  }>,
): string {
  return steps
    .map((step) => {
      const trigger =
        step.afterCompletedWeeks != null
          ? step.afterCompletedWeeks === 0
            ? 'ab Start'
            : `${step.afterCompletedWeeks} W`
          : `${step.afterCompletedMonths} M`;
      return `${trigger} ${Number(step.percent).toLocaleString('de-DE')} %`;
    })
    .join(' → ');
}
